import React, { FC, useCallback, useMemo } from 'react'

import { FolderIcon, FolderOpenIcon } from '@heroicons/react/20/solid'
import classNames from 'classnames'
import { useDrop } from 'react-dnd'

import { useAppDispatch, useAppSelector } from '@/client/hooks'
import { Checkbox, CheckboxValue } from '@/components/Checkbox'
import {
  FileManagerFile,
  ROOT_FOLDERS,
  clearFiles,
  deselectFiles,
  selectFiles,
} from '@/features/FileManager/fileManagerSlice'
import useDocuments from '@/features/FileManager/useDocuments'
import { getColor } from '@/key-components/utils/KeyColors'
import { useUpdateUserDocumentMutation } from '@/services/documents'
import { UserDocument } from '@/types/unlock'

interface TreeViewerFolderProps {
  folder: string
  parentFolder?: string
  opened: boolean
  selected: boolean
  fileCount: number
  level?: number
  children?: React.ReactNode
  hasNewFiles?: boolean
  onFolderClicked: (folder: string) => void
  onFolderSelected: (folder: string, parentFolder: string) => void
  customIcon?: JSX.Element
}

export const TreeViewerFolder: FC<TreeViewerFolderProps> = (props) => {
  const {
    folder,
    parentFolder = '',
    opened,
    selected,
    fileCount,
    level = 0,
    onFolderClicked,
    onFolderSelected,
    children,
    customIcon,
    hasNewFiles,
  } = props

  const dispatch = useAppDispatch()

  const [updateDoc] = useUpdateUserDocumentMutation()

  const { data: documents } = useDocuments()

  const selectedFiles = useAppSelector((state) => state.fileManager.selectedFiles)

  const canBeMoved = (origin: string, destination: string) => {
    if (origin === null && ROOT_FOLDERS.includes(destination)) return false
    return origin !== destination
  }

  const handleDrop = useCallback(
    (droppedFile) => {
      const moveFile = (file: FileManagerFile) => {
        // If folder doesn't change, don't do anything
        if (!canBeMoved(file.tag, folder)) return

        const payload: Partial<UserDocument> = { uuid: file.uuid }

        payload.tag = parentFolder ? `${parentFolder}/${folder}` : folder

        updateDoc(payload)
      }

      // If files are selected, move those. Else move the dragged file (even if not selected)
      if (selectedFiles.length) {
        selectedFiles.forEach((file) => {
          moveFile(file)
        })

        dispatch(clearFiles())
      } else {
        moveFile(droppedFile)
      }
    },
    [dispatch, folder, selectedFiles, updateDoc]
  )

  const [{ isOver, canDrop }, dropRef] = useDrop<FileManagerFile, any, any>(() => {
    return {
      accept: 'file',
      drop: (file) => handleDrop(file),
      canDrop: (file) => canBeMoved(file.tag, folder),
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }
  }, [handleDrop])

  const folderClasses = classNames({
    'flex gap-1 items-center justify-start py-2 px-2 border border-dashed border-transparent': true,
    'text-sky-400 font-bold': selected,
    'border-sky-400 border-dashed': isOver,
    'bg-sky-300': isOver && canDrop,
    'bg-gray-400': isOver && !canDrop,
  })

  const folderFiles = useMemo(() => {
    if (ROOT_FOLDERS.includes(folder)) return documents

    return documents.filter((file) => file.tag === `${parentFolder}/${folder}`)
  }, [documents, folder])

  const handleCheckbox = () => {
    switch (checkboxValue) {
      case 'unchecked':
      case 'indeterminate':
        dispatch(selectFiles(folderFiles))
        break
      case 'checked':
        dispatch(deselectFiles(folderFiles))
    }
  }

  const checkboxValue: CheckboxValue = useMemo(() => {
    const isFileSelected = (file: FileManagerFile) => {
      return !!selectedFiles.find((it) => it.uuid === file.uuid)
    }

    if (!folderFiles.length) return 'unchecked'

    let result

    if (folderFiles.every((file) => isFileSelected(file))) {
      result = 'checked'
    } else if (folderFiles.some((file) => isFileSelected(file))) {
      result = 'indeterminate'
    } else {
      result = 'unchecked'
    }

    return result
  }, [folderFiles, selectedFiles])

  return (
    <div>
      <div
        className={folderClasses}
        ref={dropRef}
      >
        <div style={{ paddingLeft: `${level * 10}px` }}>
          <Checkbox
            color={'rgb(30 64 175)'}
            onClick={() => handleCheckbox()}
            value={checkboxValue}
            disabled={!folderFiles.length}
          />
        </div>

        <div
          onClick={() => onFolderSelected(folder, parentFolder)}
          className='flex w-full grow-1 cursor-pointer gap-1 items-center text-ellipsis overflow-hidden whitespace-nowrap'
        >
          <div
            className='shrink-0'
            onClick={() => onFolderClicked(folder)}
          >
            {customIcon ? (
              customIcon
            ) : opened && children ? (
              <FolderOpenIcon
                width={20}
                fill={getColor('blue')}
              />
            ) : (
              <FolderIcon
                width={20}
                fill={getColor('blue', 'l3')}
              />
            )}
          </div>
          {folder} {fileCount && <span className='font-normal'>({fileCount})</span>}{' '}
          {hasNewFiles && (
            <span className='bg-blue-100 text-blue-800 text-xs font-medium me-2 px-2.5 py-0.5 rounded-full dark:bg-blue-900 dark:text-blue-300'>
              new
            </span>
          )}
        </div>
      </div>
      {opened && children}
    </div>
  )
}
